// refund.service — guard + validasi Pengembalian Dana (CS ajukan → Finance verifikasi).
import { currentUser } from '@core/session';
import { canVerify } from '@domain/access';
import { paidSoFar, piutangTiket } from '@domain/money';
import { todayISO } from '@domain/format';
import { logAudit } from '@core/audit';
import type { Refund } from '@core/types';
import * as repo from './refund.repo';
import { tarikBonusTiket } from '@modules/crm/afiliasi.service';

const REFUND_ROLES = ['Owner', 'Manager', 'SPV', 'CS'];

export interface RefundPrefill { paid: number; debt: number; custNama: string; custWa: string; unit: string; usul: { amount: number; note?: string; by?: string } | null; }

/** Info untuk dialog refund (default jumlah = usul teknisi bila ada, else kas yang sudah dibayar). */
export async function prefill(no: string): Promise<RefundPrefill> {
  const { ticket: t, custNama, custWa, unit, refundUsul } = await repo.getForRefund(no);
  return { paid: paidSoFar(t), debt: piutangTiket(t), custNama, custWa, unit, usul: refundUsul };
}

/** CS ajukan pengembalian dana. Validasi setia v0.18. */
export async function submitRefund(no: string, amount: number, method: string, reason: string, nama: string): Promise<void> {
  const u = currentUser(); if (!u) throw new Error('Sesi tidak valid.');
  if (!REFUND_ROLES.includes(u.role)) throw new Error('Tidak punya izin memproses pengembalian.');
  if (!nama.trim()) throw new Error('Nama penerima wajib diisi.');
  const { ticket: t, tglAmbilISO, caraBayar } = await repo.getForRefund(no);
  if (t.status !== 'Siap Diambil') throw new Error('Refund hanya dari status Siap Diambil.');
  if (t.hasil !== 'tidakbisa' && t.hasil !== 'batal') throw new Error('Refund hanya untuk servis tidak bisa diperbaiki / dibatalkan.');
  const paid = paidSoFar(t), debt = piutangTiket(t);
  if (amount < 0) throw new Error('Jumlah tidak valid.');
  if (amount === 0 && debt === 0 && paid === 0) throw new Error('Tidak ada kas atau hutang untuk diproses.');
  if (amount > paid) throw new Error(`Jumlah refund tidak boleh melebihi total pembayaran lintas sesi (Rp ${paid.toLocaleString('id-ID')}).`);
  const refund: Refund = {
    amount, method, reason: reason.trim() || 'Unit tidak bisa diperbaiki — dana dikembalikan sesuai kesepakatan.',
    status: 'pending', by: u.nama,
    prevPaid: paid, prevMethod: caraBayar ?? undefined, prevDate: tglAmbilISO ?? undefined, // utk Nota Pengembalian
  };
  await repo.submitRefund(no, t.outlet, refund, nama.trim(), tglAmbilISO ?? todayISO());
  await logAudit({ action: 'REFUND_SUBMIT', module: 'ticket', refId: no, outlet: t.outlet, reason: refund.reason, meta: { amount, method } });
}

/** Finance verifikasi refund (Owner/Manager/Admin Finance). */
export async function verifyRefund(no: string): Promise<void> {
  const u = currentUser();
  if (!u || !canVerify(u.role)) throw new Error('Verifikasi refund hanya Finance / Owner / Manager.');
  await repo.verifyRefund(no);
  await logAudit({ action: 'REFUND_VERIFY', module: 'ticket', refId: no });
  // Poin servis ikut hilang sendiri (dihitung dari omzet). Bonus AFILIASI tidak — ia
  // pemberian tetap, jadi ditarik di sini lengkap dengan alasannya.
  try { await tarikBonusTiket(no, `Servis ${no} dikembalikan dananya`); } catch { /* penarikan menyusul */ }
}
